/**
 * Primary-sidebar chrome controller.
 *
 * Owns every visual aspect of the primary sidebar shell that is NOT its
 * content: which presentation it uses, whether it is open, the outside-click
 * scrim, the drag-to-resize handle and the persisted width. It is the single
 * writer of the `primarySidebar-hidden` class — `ActivityBarManager` used to
 * toggle that class in three places, each with its own scrim bookkeeping.
 *
 * Two presentations, picked from the viewport:
 *
 *   docked (>= 1024px) — the sidebar is a real grid column between the
 *     activity bar and the content area: content reflows around it, the width
 *     is drag-resizable and remembered, and there is no scrim (clicks reach
 *     the editor directly).
 *   drawer (< 1024px)  — the pre-existing behaviour: an absolutely positioned
 *     overlay that slides in over the content with a transparent scrim, so the
 *     first click outside dismisses it.
 *
 * The layout CSS lives in the hosts (`punica-editor-browser`,
 * `punica-editor-electron`) and knows only the drawer. The docked rules are
 * injected from here, keyed on `#punica-editor[data-sidebar-mode='docked']` —
 * two ids, so they outrank the hosts' single-id rules without touching them.
 */

import sidebarDockCss from './sidebarDock.css';

const STYLE_ID = 'shell-classic-sidebar-dock-style';
const SCRIM_ID = 'primarySidebar-scrim';
const RESIZER_ID = 'primarySidebar-resizer';
const HIDDEN_CLASS = 'primarySidebar-hidden';
const LAYOUT_ROOT_ID = 'punica-editor';

/** Viewport at/above which the sidebar docks (iPad landscape is the boundary). */
const DOCK_QUERY = '(min-width: 1024px)';

const WIDTH_VAR = '--primary-sidebar-width';
const WIDTH_SETTINGS_KEY = 'sidebar.width';
const DEFAULT_WIDTH = 300;
const MIN_WIDTH = 180;
const MAX_WIDTH = 720;
/** Never let the sidebar eat more than this share of a narrow desktop window. */
const MAX_WIDTH_RATIO = 0.6;
const KEY_STEP = 16;
const KEY_STEP_LARGE = 64;
/** Used when the activity bar cannot be measured (headless DOM). */
const ACTIVITY_BAR_FALLBACK_WIDTH = 48;

/**
 * The ids and measurements `./sidebarDock.css` writes out as literals. See the
 * matching export in `../appBar/index.js` — the drift guard in
 * `__tests__/sidebarDock.test.ts` checks the injected sheet against these.
 */
export const CSS_TOKENS = Object.freeze({
  LAYOUT_ROOT_ID,
  RESIZER_ID,
  WIDTH_VAR,
  DEFAULT_WIDTH,
  ACTIVITY_BAR_WIDTH: ACTIVITY_BAR_FALLBACK_WIDTH
});

type SidebarMode = 'docked' | 'drawer';

function settingsManager(): any {
  return (globalThis as any).punica?.extensions?.settings?.manager ?? null;
}

class SidebarDockController {
  #initialized = false;
  #mode: SidebarMode = 'drawer';
  #visible = false;
  /** User intent, unclamped — the viewport clamp is applied when it is written. */
  #width = DEFAULT_WIDTH;
  #scrim: HTMLElement | null = null;
  #resizer: HTMLElement | null = null;
  #dismiss: (() => void) | null = null;
  #dragging = false;

  /**
   * Inject the docked stylesheet, restore the remembered width and start
   * tracking the viewport. Idempotent and safe to call before the layout has
   * rendered — `#apply` no-ops until the slot exists and runs again on the
   * next `setVisible`.
   */
  init(): void {
    if (this.#initialized) return;
    this.#initialized = true;
    this.#injectStyle();
    this.#restoreWidth();
    this.#watchViewport();
    this.#apply();
  }

  getMode(): SidebarMode {
    return this.#mode;
  }

  isVisible(): boolean {
    return this.#visible;
  }

  /** The single writer of the sidebar's open/closed state. */
  setVisible(visible: boolean): void {
    this.#visible = visible;
    if (!this.#initialized) {
      this.init();
      return;
    }
    this.#apply();
  }

  /**
   * Register what a press on the drawer scrim should do. `ActivityBarManager`
   * routes it through `Views.manager.hide`, the single visibility authority.
   */
  setDismissHandler(handler: () => void): void {
    this.#dismiss = handler;
  }

  /** Current width in px, clamped to the viewport. */
  getWidth(): number {
    return this.#clamp(this.#width);
  }

  /** Add the docked rules to the document once (hosts ship only the drawer). */
  #injectStyle(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = sidebarDockCss;
    document.head.appendChild(style);
  }

  // ── DOM lookups ──────────────────────────────────────────────────────────

  #sidebar(): HTMLElement | null {
    try {
      const slot = (globalThis as any).punica?.shell?.Layout?.manager?.getSlot?.(
        'primarySidebar'
      );
      return (slot?.content as HTMLElement | undefined) ?? null;
    } catch {
      return null;
    }
  }

  #root(): HTMLElement | null {
    const sidebar = this.#sidebar();
    return (
      (sidebar?.parentElement as HTMLElement | null) ??
      document.getElementById(LAYOUT_ROOT_ID)
    );
  }

  #activityBarWidth(): number {
    try {
      const slot = (globalThis as any).punica?.shell?.Layout?.manager?.getSlot?.(
        'activityBar'
      );
      const width = (
        slot?.content as HTMLElement | undefined
      )?.getBoundingClientRect?.().width;
      if (typeof width === 'number' && width > 0) return width;
    } catch {
      // fall through to the fixed bar width
    }
    return ACTIVITY_BAR_FALLBACK_WIDTH;
  }

  // ── State application ────────────────────────────────────────────────────

  #apply(): void {
    const sidebar = this.#sidebar();
    const root = this.#root();
    if (!sidebar || !root) return;

    root.dataset.sidebarMode = this.#mode;
    root.dataset.sidebarCollapsed = this.#visible ? 'false' : 'true';
    // The class stays authoritative in both modes: the untouched host CSS uses
    // it to slide the drawer, and it keeps the DOM readable in docked mode.
    sidebar.classList.toggle(HIDDEN_CLASS, !this.#visible);
    this.#applyWidth(root);

    if (this.#mode === 'docked') {
      this.#hideScrim();
      this.#mountResizer(sidebar);
    } else {
      this.#unmountResizer();
      if (this.#visible) this.#showScrim(root);
      else this.#hideScrim();
    }
  }

  #applyWidth(root: HTMLElement | null): void {
    const target = root ?? this.#root();
    if (!target) return;
    target.style.setProperty(WIDTH_VAR, `${this.#clamp(this.#width)}px`);
  }

  #clamp(width: number): number {
    const viewport = typeof window !== 'undefined' ? window.innerWidth : 0;
    const max =
      viewport > 0
        ? Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, viewport * MAX_WIDTH_RATIO))
        : MAX_WIDTH;
    const clamped = Math.min(Math.max(width, MIN_WIDTH), max);
    return Math.round(clamped);
  }

  // ── Width persistence ────────────────────────────────────────────────────

  /**
   * Read the remembered width from the settings store (`'system'` pseudo
   * extension, the same tier the dock-panel layout store uses). Reads are
   * synchronous; a value written before `settings.load()` resolved simply
   * isn't there yet and the default applies.
   */
  #restoreWidth(): void {
    try {
      const raw = settingsManager()?.get?.('system', WIDTH_SETTINGS_KEY);
      const parsed = typeof raw === 'string' ? Number(raw) : raw;
      if (typeof parsed === 'number' && Number.isFinite(parsed) && parsed > 0) {
        this.#width = parsed;
      }
    } catch {
      // keep the default width
    }
  }

  #persistWidth(): void {
    const settings = settingsManager();
    if (!settings?.set) return;
    void Promise.resolve(
      settings.set('system', WIDTH_SETTINGS_KEY, this.#clamp(this.#width), 'global')
    ).catch((err: unknown) => {
      // eslint-disable-next-line no-console
      console.warn('[SidebarDock] failed to persist sidebar width', err);
    });
  }

  #setWidth(width: number, persist: boolean): void {
    this.#width = this.#clamp(width);
    this.#applyWidth(null);
    if (persist) this.#persistWidth();
  }

  // ── Viewport tracking ────────────────────────────────────────────────────

  #watchViewport(): void {
    const mql =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(DOCK_QUERY)
        : null;

    this.#mode = mql?.matches ? 'docked' : 'drawer';
    if (typeof window === 'undefined') return;

    const onModeChange = (event: { matches: boolean }) => {
      const next: SidebarMode = event.matches ? 'docked' : 'drawer';
      if (next === this.#mode) return;
      this.#mode = next;
      this.#apply();
    };

    if (mql?.addEventListener) {
      mql.addEventListener('change', (event) => onModeChange(event));
    } else if (typeof (mql as any)?.addListener === 'function') {
      (mql as any).addListener(onModeChange);
    }

    // Re-clamp on window resize so a docked sidebar can never grow wider than
    // the window allows. `#width` keeps the user's intent, so widening the
    // window restores the width they chose.
    window.addEventListener('resize', () => this.#applyWidth(null));
  }

  // ── Scrim (drawer mode only) ─────────────────────────────────────────────

  /**
   * Transparent scrim ("glass") over the editor while the drawer is open. A
   * press on it closes the drawer — the click-outside-to-dismiss gesture —
   * without document-level hit-testing, which misses clicks inside iframes and
   * shadow roots (e.g. notebook output cells). It sits above the main content
   * but below the activity bar, primary sidebar and contentSidebar, so those
   * stay interactive while it intercepts editor-surface clicks.
   */
  #showScrim(root: HTMLElement): void {
    if (!this.#scrim) {
      const scrim = document.createElement('div');
      scrim.id = SCRIM_ID;
      scrim.setAttribute(
        'style',
        'position:absolute;inset:0;background:transparent;' +
          'z-index:calc(var(--zindex-drawer, 1200) - 1);'
      );
      scrim.addEventListener('pointerdown', () => this.#dismiss?.());
      this.#scrim = scrim;
    }
    if (this.#scrim.parentElement !== root) root.appendChild(this.#scrim);
    this.#scrim.style.display = 'block';
  }

  /** Hide the scrim without destroying it (reused on the next reveal). */
  #hideScrim(): void {
    if (this.#scrim) this.#scrim.style.display = 'none';
  }

  // ── Resize handle (docked mode only) ─────────────────────────────────────

  #mountResizer(sidebar: HTMLElement): void {
    if (!this.#resizer) {
      const resizer = document.createElement('div');
      resizer.id = RESIZER_ID;
      resizer.setAttribute('role', 'separator');
      resizer.setAttribute('aria-orientation', 'vertical');
      resizer.setAttribute('aria-label', 'Resize primary side bar');
      resizer.tabIndex = 0;
      resizer.addEventListener('pointerdown', (event) =>
        this.#beginDrag(event as PointerEvent)
      );
      resizer.addEventListener('keydown', (event) =>
        this.#onResizerKeydown(event as KeyboardEvent)
      );
      resizer.addEventListener('dblclick', () =>
        this.#setWidth(DEFAULT_WIDTH, true)
      );
      this.#resizer = resizer;
    }
    if (this.#resizer.parentElement !== sidebar) sidebar.appendChild(this.#resizer);
    this.#resizer.style.display = this.#visible ? 'block' : 'none';
  }

  #unmountResizer(): void {
    const parent = this.#resizer?.parentElement;
    if (parent && this.#resizer) parent.removeChild(this.#resizer);
  }

  #beginDrag(event: PointerEvent): void {
    if (this.#mode !== 'docked' || !this.#visible || this.#dragging) return;
    event.preventDefault();
    this.#dragging = true;

    const offset = this.#activityBarWidth();
    const root = this.#root();
    if (root) root.dataset.sidebarResizing = 'true';
    try {
      this.#resizer?.setPointerCapture?.(event.pointerId);
    } catch {
      // pointer capture is a nicety; the window listeners do the real work
    }

    // A pointer stream can outpace the display, and every width write relayouts
    // the whole workbench (editor, panel, right sidebar). Coalesce to one write
    // per frame so a fast drag cannot queue up redundant layouts.
    let pendingWidth: number | null = null;
    let frame: number | null = null;
    const raf =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame
        : null;

    const flush = () => {
      frame = null;
      if (pendingWidth === null) return;
      const width = pendingWidth;
      pendingWidth = null;
      this.#setWidth(width, false);
    };

    const onMove = (moveEvent: PointerEvent) => {
      pendingWidth = moveEvent.clientX - offset;
      if (!raf) {
        flush();
        return;
      }
      if (frame === null) frame = raf(flush);
    };
    const onEnd = () => {
      window.removeEventListener('pointermove', onMove as EventListener);
      window.removeEventListener('pointerup', onEnd);
      window.removeEventListener('pointercancel', onEnd);
      if (frame !== null && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(frame);
        frame = null;
      }
      flush();
      document.documentElement.style.removeProperty('user-select');
      if (root) delete root.dataset.sidebarResizing;
      this.#dragging = false;
      this.#persistWidth();
    };

    window.addEventListener('pointermove', onMove as EventListener);
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
    // Dragging over text would otherwise select it across the whole workbench.
    document.documentElement.style.setProperty('user-select', 'none');
  }

  #onResizerKeydown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const step = event.shiftKey ? KEY_STEP_LARGE : KEY_STEP;
    const delta = event.key === 'ArrowLeft' ? -step : step;
    this.#setWidth(this.#clamp(this.#width) + delta, true);
  }
}

export const sidebarDock = new SidebarDockController();
export type { SidebarMode };
